import type { LayoutConfig, ModuleConfig } from './types'
import { isElectron } from '@/utils/env'
import { isMacOS } from '@/utils/ua'
import { useDeviceStore } from '@/stores'
/**
 * VT二代 布局配置
 */
function getVtGen2Modules(): ModuleConfig[] {
  // 客户端 window 特有
  const electronWindowsModules: ModuleConfig[] = [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 8,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'pointer',
      component: 'PointerSpeed',
      colStart: 9,
      colSpan: 12,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    // 滚轮速度
    {
      id: 'debounce',
      component: 'WheelSpeed',
      colStart: 9,
      colSpan: 7,
      rowStart: 8,
      rowSpan: 5,
      size: 'normal',
    },
    // 按键防抖
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 16,
      colSpan: 9,
      rowStart: 8,
      rowSpan: 5,
      size: 'compact',
    },
  ]
  const electronMacModules: ModuleConfig[] = [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 8,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'pointer',
      component: 'PointerSpeed',
      colStart: 9,
      colSpan: 12,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    // 按键防抖
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 9,
      colSpan: 16,
      rowStart: 8,
      rowSpan: 5,
      size: 'compact',
    },
  ]
  const webModules: ModuleConfig[] = [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 20,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 9,
      colSpan: 16,
      rowStart: 8,
      rowSpan: 5,
      size: 'normal',
    },
  ]

  const modules = isElectron()
    ? isMacOS
      ? electronMacModules
      : electronWindowsModules
    : webModules

  return [
    ...modules,
    {
      id: 'liftoff',
      component: 'LiftoffHeight',
      colStart: 21,
      colSpan: 4,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'angle',
      component: 'SensorAngle',
      colStart: 1,
      colSpan: 8,
      rowStart: 6,
      rowSpan: 7,
      size: 'normal',
    },
    {
      id: 'glass',
      component: 'GlassTracking',
      colStart: 9,
      colSpan: 16,
      rowStart: 6,
      rowSpan: 2,
      size: 'normal',
    },
  ]
}

// VT二代鼠标
export const VT_GEN2_LAYOUT: LayoutConfig = {
  totalColumns: 24,
  totalRows: 12,
  rowHeight: 'repeat(12, 51px)',
  modules: getVtGen2Modules(),
}

// 网吧鼠标
export const VT_GEN1_LAYOUT: LayoutConfig = {
  totalColumns: 24,
  totalRows: 12,
  rowHeight: 'repeat(12, 51px)',
  modules: [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 8,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'pointer',
      component: 'PointerSpeed',
      colStart: 9,
      colSpan: 12,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'liftoff',
      component: 'LiftoffHeight',
      colStart: 21,
      colSpan: 4,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'angle',
      component: 'SensorAngle',
      colStart: 1,
      colSpan: 8,
      rowStart: 6,
      rowSpan: 7,
      size: 'expanded',
    },
    // 玻璃追踪
    {
      id: 'glass',
      component: 'GlassTracking',
      colStart: 9,
      colSpan: 16,
      rowStart: 6,
      rowSpan: 2,
      size: 'compact',
    },
    // 滚轮速度
    {
      id: 'debounce',
      component: 'WheelSpeed',
      colStart: 9,
      colSpan: 7,
      rowStart: 8,
      rowSpan: 5,
      size: 'normal',
    },
    // 按键防抖
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 16,
      colSpan: 9,
      rowStart: 8,
      rowSpan: 5,
      size: 'compact',
    },
  ],
}

/**
 * VT-SE 布局配置（无 LiftoffHeight / SensorAngle / GlassTracking，
 * 剩余模块占满整行宽度）
 */
function getVtSeModules(): ModuleConfig[] {
  // 客户端 window 特有
  const electronWindowsModules: ModuleConfig[] = [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 8,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'pointer',
      component: 'PointerSpeed',
      colStart: 9,
      colSpan: 16,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    // 滚轮速度
    {
      id: 'debounce',
      component: 'WheelSpeed',
      colStart: 1,
      colSpan: 8,
      rowStart: 6,
      rowSpan: 5,
      size: 'normal',
    },
    // 按键防抖
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 9,
      colSpan: 16,
      rowStart: 6,
      rowSpan: 5,
      size: 'compact',
    },
  ]
  const electronMacModules: ModuleConfig[] = [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 8,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'pointer',
      component: 'PointerSpeed',
      colStart: 9,
      colSpan: 16,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    // 按键防抖
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 1,
      colSpan: 24,
      rowStart: 6,
      rowSpan: 5,
      size: 'compact',
    },
  ]
  const webModules: ModuleConfig[] = [
    {
      id: 'switches',
      component: 'SwitchGroup',
      colStart: 1,
      colSpan: 24,
      rowStart: 1,
      rowSpan: 5,
      size: 'normal',
    },
    {
      id: 'debounce',
      component: 'KeyDebounce',
      colStart: 1,
      colSpan: 24,
      rowStart: 6,
      rowSpan: 5,
      size: 'normal',
    },
  ]

  return isElectron() ? (isMacOS ? electronMacModules : electronWindowsModules) : webModules
}

// VT-SE 鼠标
export const VT_SE_LAYOUT: LayoutConfig = {
  totalColumns: 24,
  totalRows: 12,
  rowHeight: 'repeat(12, 51px)',
  modules: getVtSeModules(),
}

/**
 * 根据设备型号获取布局配置
 * TODO: 后续从 manifest 获取真实型号，替换写死逻辑
 */
export function getLayoutByModel(): LayoutConfig {
  // 未来根据 model 做映射:
  // switch (model) {
  //   case 'S2-3950': return S2_3950_LAYOUT
  //   case 'S4-3398': return S4_3398_LAYOUT
  //   default: return VT_GEN2_LAYOUT
  // }
  const deviceStore = useDeviceStore()
  const manifest = deviceStore.currentDevice?.manifest
  if (!manifest) {
    return VT_GEN2_LAYOUT
  }
  if (manifest.sensor === 'PAW3311') {
    return VT_SE_LAYOUT
  }
  return VT_GEN2_LAYOUT
}
